import { Risk, RiskAccount, RiskBand, RiskLatestUpdate, RiskResponseType, RiskStatus, RiskTrend, RiskUpdateType } from './risk-register.models';

/** Score bands from the customer's sheet (spec §3.5). */
export function bandOf(score: number): RiskBand {
  if (score >= 16) return 'critical';
  if (score >= 10) return 'high';
  if (score >= 5) return 'medium';
  return 'low';
}

export function scoreOf(probability: number, impact: number): number {
  return Number(probability) * Number(impact);
}

export function trendOf(before: number, after: number): RiskTrend {
  if (after > before) return 'up';
  if (after < before) return 'down';
  return 'same';
}

/** Chip classes per band — the punch-list severity tints, copied into the list scss. */
export const BAND_MAP: Record<RiskBand, { label: string; bgColor: string; textColor: string }> = {
  low: { label: 'Low', bgColor: 'bg-low', textColor: 'txt-low' },
  medium: { label: 'Medium', bgColor: 'bg-medium', textColor: 'txt-medium' },
  high: { label: 'High', bgColor: 'bg-high', textColor: 'txt-high' },
  critical: { label: 'Critical', bgColor: 'bg-critical', textColor: 'txt-critical' },
};

export const STATUS_MAP: Record<RiskStatus, { label: string; bgColor: string; textColor: string }> = {
  pending: { label: 'Pending approval', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
  open: { label: 'Open', bgColor: 'bg-draft', textColor: 'txt-draft' },
  closed: { label: 'Closed', bgColor: 'bg-draft', textColor: 'txt-draft' },
  converted: { label: 'Converted', bgColor: 'bg-aashtoware', textColor: 'txt-aashtoware' },
  rejected: { label: 'Rejected', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
};

export const RESPONSE_TYPES: { value: RiskResponseType; label: string }[] = [
  { value: 'acceptance', label: 'Accept' },
  { value: 'avoidance', label: 'Avoid' },
  { value: 'contingency', label: 'Contingency' },
  { value: 'mitigation', label: 'Mitigate' },
  { value: 'transfer', label: 'Transfer' },
];

export const RESPONSE_TYPE_LABEL: Record<string, string> = RESPONSE_TYPES
  .reduce((m, o) => ({ ...m, [o.value]: o.label }), {} as Record<string, string>);

/** Uppercase action labels in the Discussion-tab style (the template adds the colon). */
export const UPDATE_LABELS: Record<RiskUpdateType, string> = {
  created: 'Risk created',
  proposed: 'Proposed',
  approved: 'Approved',
  rejected: 'Rejected',
  updated: 'Edited',
  score_changed: 'Score changed',
  update: 'Update',
  closed: 'Closed',
  reopened: 'Reopened',
  converted: 'Converted to issue',
  reassigned: 'Owner changed',
};

export type UpdateIcon = 'ok' | 'up' | 'down' | 'same' | 'closed' | 'conv' | 'rej' | 'edit';

/** Icon per event type; score_changed picks up/down from the trend at render time. */
export const UPDATE_ICONS: Record<RiskUpdateType, UpdateIcon> = {
  created: 'ok', proposed: 'same', approved: 'ok', rejected: 'rej', updated: 'edit', score_changed: 'same',
  update: 'same', closed: 'closed', reopened: 'ok', converted: 'conv', reassigned: 'edit',
};

export function updateIcon(type: RiskUpdateType, trend?: RiskTrend | null): UpdateIcon {
  if (type === 'score_changed') return trend === 'up' ? 'up' : trend === 'down' ? 'down' : 'same';
  return UPDATE_ICONS[type] || 'same';
}

/** The trend inside the list's score pill. Bare arrow when the delta is unknown (imported updates). */
export function trendArrow(latest: RiskLatestUpdate | null): { arrow: string; delta: string; cls: string } {
  const trend = latest?.trend || 'same';
  const arrow = trend === 'up' ? '▲' : trend === 'down' ? '▼' : '—';
  let delta = '';
  if (latest && latest.score_before !== null && latest.score_after !== null && latest.score_before !== undefined && latest.score_after !== undefined) {
    const d = latest.score_after - latest.score_before;
    if (d > 0) delta = `+${d}`;
    else if (d < 0) delta = `−${Math.abs(d)}`;
  }
  return { arrow, delta, cls: `trend-${trend}` };
}

export function ownerName(risk: Pick<Risk, 'owner'>): string {
  const o = risk?.owner;
  return o ? `${o.first_name || ''} ${o.last_name || ''}`.trim() || o.email || 'Unassigned' : 'Unassigned';
}

export function initials(account: RiskAccount | null | undefined): string {
  if (!account) return '?';
  const a = (account.first_name || '').charAt(0);
  const b = (account.last_name || '').charAt(0);
  return `${a}${b}`.toUpperCase() || '?';
}

export function isOpen(risk: Pick<Risk, 'status'>): boolean {
  return risk?.status === 'open';
}

export const RISK_STATUS_FILTERS: { label: string; value: string }[] = [
  { label: 'Open', value: 'open' },
  { label: 'Pending', value: 'pending' },
  { label: 'Closed', value: 'closed' },
  { label: 'Converted', value: 'converted' },
  { label: 'All', value: 'all' },
];

/**
 * The api answers errors as `{ status, error }`: a string, or for Joi validation
 * (422) an object of `{ field: { msg } }`. Also accepts `message` or a bare string.
 */
export function serverErrorMessage(body: unknown, fallback: string): string {
  const b = body as { error?: unknown; message?: unknown } | string | null | undefined;
  const raw = typeof b === 'string' ? b : b?.error || b?.message;
  if (typeof raw === 'string') return raw || fallback;
  if (raw && typeof raw === 'object') {
    const msgs = Object.values(raw as Record<string, { msg?: unknown }>).map(v => v?.msg).filter((m): m is string => typeof m === 'string' && !!m);
    if (msgs.length) return msgs.join('; ');
  }
  return fallback;
}

/** Tag that keeps risk attachments apart in the shared ProjectsService.mediaData feed. */
export const RISK_ATTACHMENT_TAG = 'risk-attachments';

/** File picker `accept` list for risk / risk-update attachments (images and documents; no video). */
export const RISK_ATTACHMENT_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt';

export interface RiskMediaFile { id: string; original_name?: string; mime?: string; fileType?: string; tags?: string[]; [key: string]: unknown }

/**
 * Shape a stored or freshly uploaded file for app-pictures-section-with-tags: it renders
 * by `fileType` and filters by `tags[0]` (same normalisation as the punchlist form).
 */
export function toRiskMedia(f: RiskMediaFile): RiskMediaFile {
  const mime = typeof f.mime === 'string' ? f.mime : '';
  const fileType = f.fileType || (mime.startsWith('image/') ? 'picture' : mime.startsWith('video/') ? 'video' : 'document');
  return { ...f, fileType, tags: [RISK_ATTACHMENT_TAG] };
}

export interface SkippedGroup { reason: string; count: number; ids: string[]; text: string; idsText: string }

/** Max IDs listed under a collapsed import-skip line before "+N more". */
export const SKIPPED_IDS_SHOWN = 10;

/** Collapse the import preview's per-risk skips into one line per distinct reason (first-seen order). */
export function groupSkipped(skipped: { risk_no: string; reason: string }[]): SkippedGroup[] {
  const byReason = new Map<string, string[]>();
  for (const s of skipped || []) {
    const ids = byReason.get(s.reason) || [];
    ids.push(s.risk_no);
    byReason.set(s.reason, ids);
  }
  return [...byReason.entries()].map(([reason, ids]) => {
    const count = ids.length;
    const one = count === 1;
    let text: string;
    if (reason === 'already on this project') text = `${count} risk${one ? '' : 's'} already on this project — skipped`;
    else if (reason === 'duplicate ID in the sheet') text = `${count} duplicate ID${one ? '' : 's'} in the sheet — skipped`;
    else text = `${count} risk${one ? '' : 's'} skipped: ${reason}`;
    const shown = ids.slice(0, SKIPPED_IDS_SHOWN).join(', ');
    const idsText = `IDs: ${shown}${count > SKIPPED_IDS_SHOWN ? ` +${count - SKIPPED_IDS_SHOWN} more` : ''}`;
    return { reason, count, ids, text, idsText };
  });
}
